import React, { useRef } from 'react';
import styled from 'styled-components/macro';
import { hexToRgb } from 'helpers/hexToRgb';
import { createPortal } from 'react-dom';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import { msTransition } from 'helpers/msTransition';
import { theme } from 'theme';

interface IProps {
  show: boolean;
}

const modalTransitionDuration = theme.transition.slow;

const StyledAnimation = styled.div<{ animationStatus: TransitionStatus }>`
  transition: ${msTransition(modalTransitionDuration)};
  opacity: ${({ animationStatus }) => {
    switch (animationStatus) {
      case 'entering':
        return 1;
      case 'entered':
        return 1;
      case 'exiting':
        return 0;
      case 'exited':
        return 0;
      default:
        return '';
    }
  }};
`;

const StyledOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 30;
  background: ${({ theme }) => `rgba(${hexToRgb(theme.colors.black, 'string')}, 0.9)`};
`;

const StyledModalWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing[2]};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  position: fixed;
  z-index: 40;
  max-width: ${({ theme }) => `${theme.breakpoints.md}px`};
  max-height: 100vh;
  width: 100%;
  overflow: auto;
`;

const StyledModal = styled.div`
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing[4]};

  @media (min-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    padding: ${({ theme }) => `${theme.spacing[6]} ${theme.spacing[12]}`};
  }
`;

const Modal: React.FC<IProps> = ({ show, children }) => {
  const nodeRef = useRef(null);

  return createPortal((
    <Transition in={show} timeout={modalTransitionDuration} nodeRef={nodeRef} unmountOnExit mountOnEnter>
      {(state) => (
        <StyledAnimation animationStatus={state} ref={nodeRef}>
          <StyledOverlay />
          <StyledModalWrapper>
            <StyledModal>
              {children}
            </StyledModal>
          </StyledModalWrapper>
        </StyledAnimation>
      )}
    </Transition>
  ), document.body);
};

export { Modal };
